import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './MapPickerModal.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng) });
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  if (target) map.flyTo(target, 14, { duration: 1.2 });
  return null;
};

const MapPickerModal = ({ onConfirm, onClose }) => {
  const [marker, setMarker] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState('');
  const [defaultCenter, setDefaultCenter] = useState([20, 0]);
  const [defaultZoom, setDefaultZoom] = useState(2);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        const latlng = { lat: latitude, lng: longitude };
        setDefaultCenter([latitude, longitude]);
        setDefaultZoom(14);
        setMarker(latlng);
        setFlyTarget(latlng);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const handleSearch = async (value) => {
    setQuery(value);
    setSearchErr('');
    if (!value.trim()) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setResults(data);
        if (!data.length) setSearchErr('No results found.');
      } catch {
        setSearchErr('Search failed. Try again.');
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const handleSelect = (item) => {
    const latlng = { lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
    setMarker(latlng);
    setFlyTarget(latlng);
    setResults([]);
    setQuery(item.display_name);
  };

  const handleConfirm = () => {
    if (!marker) return;
    onConfirm(marker.lat.toFixed(6), marker.lng.toFixed(6));
    onClose();
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="mp-modal" onClick={e => e.stopPropagation()}>

        <div className="mp-header">
          <span className="mp-title">Pick Location</span>
          <button className="mp-close" onClick={onClose}>✕</button>
        </div>

        {/* Search */}
        <div className="mp-search-wrap">
          <div className="mp-search-box">
            <span className="mp-search-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              className="mp-search-input"
              type="text"
              placeholder="Search for a place…"
              value={query}
              onChange={e => handleSearch(e.target.value)}
              autoComplete="off"
            />
            {searching && <span className="mp-search-spinner" />}
          </div>
          {results.length > 0 && (
            <ul className="mp-results">
              {results.map((r) => (
                <li key={r.place_id} className="mp-result-item" onClick={() => handleSelect(r)}>
                  {r.display_name}
                </li>
              ))}
            </ul>
          )}
          {searchErr && <p className="mp-search-err">{searchErr}</p>}
        </div>

        <p className="mp-hint">Or tap anywhere on the map to drop a pin.</p>

        <div className="mp-map-wrap">
          <MapContainer center={defaultCenter} zoom={defaultZoom} className="mp-map" scrollWheelZoom>
          <TileLayer
  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
  attribution="&copy; OpenStreetMap &copy; Carto"
/>
            <ClickHandler onPick={(latlng) => { setMarker(latlng); setFlyTarget(null); }} />
            <FlyTo target={flyTarget} />
            {marker && <Marker position={marker} />}
          </MapContainer>
        </div>

        {marker && (
          <div className="mp-coords">
            {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
          </div>
        )}

        <div className="mp-actions">
          <button className="mp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="mp-btn-confirm" onClick={handleConfirm} disabled={!marker}>
            Confirm Location
          </button>
        </div>

      </div>
    </div>
  );
};

export default MapPickerModal;
